import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  TrendingUp, 
  Calculator, 
  GraduationCap,
  ArrowRight,
  Calendar
} from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useCallback } from "react";

const SpotlightCard = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState({ x: 0, y: 0, active: false });

  const handleMove = useCallback((e: React.MouseEvent) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    setSpot({ x: e.clientX - rect.left, y: e.clientY - rect.top, active: true });
  }, []);

  return (
    <div
      ref={cardRef}
      className={`rounded-lg ${className ?? ""}`}
      onMouseMove={handleMove}
      onMouseLeave={() => setSpot((s) => ({ ...s, active: false }))}
    >
      {spot.active && (
        <div
          className="absolute inset-0 pointer-events-none z-10 rounded-lg"
          style={{
            background: `radial-gradient(300px circle at ${spot.x}px ${spot.y}px, hsla(190,64%,51%,0.08), transparent 70%)`,
          }}
        />
      )}
      {children}
    </div>
  );
};

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const services = [
    {
      icon: Users,
      title: "Business Coaching",
      description: "Strategische Begleitung für Unternehmer:innen und Führungskräfte",
      features: [
        "Positionierung & Markenentwicklung",
        "Angebotsentwicklung & Produktstrategie",
        "Preisstrategie & Marktpositionierung",
        "Geschäftsmodell-Entwicklung",
        "Zielsetzung & Erfolgsmessung"
      ],
      link: "/business-coaching",
      color: "from-accent to-accent/80"
    },
    {
      icon: TrendingUp,
      title: "Fördermittelberatung",
      description: "Meine Beratung ist förderfähig: BAFA bis 50 %, AVGS 100 %, INQA bis zu 80 %. Ich begleite Sie von der Idee bis zur erfolgreichen Bewilligung.",
      features: [
        "AVGS (Aktivierungs- und Vermittlungsgutschein) – 100 % gefördert",
        "BAFA-Förderung – bis zu 50 % Zuschuss",
        "INQA-Coaching – bis zu 80 % Förderung",
        "KOMPASS-Beratung",
        "Antragstellung & Projektbegleitung"
      ],
      link: "/foerdermittelberatung",
      color: "from-purple-accent to-purple-accent/80"
    },
    {
      icon: Calculator,
      title: "Finanzplanung & Controlling",
      description: "Aufbau nachhaltiger Finanzstrukturen und Controlling-Systeme",
      features: [
        "BWA verstehen & interpretieren",
        "Liquiditätsplanung & Cash-Flow",
        "Kennzahlen-Systeme entwickeln",
        "Budgetplanung & Forecasting",
        "Rentabilitätsanalysen"
      ],
      link: "/finanzplanung-controlling",
      color: "from-accent to-purple-accent"
    },
    {
      icon: GraduationCap,
      title: "Seminare & Dozententätigkeit",
      description: "Praxisnahe Weiterbildung für Fach- und Führungskräfte",
      features: [
        "BWL kompakt für Nicht-Betriebswirte",
        "Lohnbuchhaltung & Personalwesen",
        "Fachwirte-Vorbereitung",
        "KI in Unternehmen",
        "Projektmanagement"
      ],
      link: "#contact",
      color: "from-purple-accent to-accent"
    }
  ];

  const businessFeaturePaths: Record<string, string> = {
    "Positionierung & Markenentwicklung": "/business-coaching/positionierung-marke",
    "Angebotsentwicklung & Produktstrategie": "/business-coaching/angebotsentwicklung-produktstrategie",
    "Preisstrategie & Marktpositionierung": "/business-coaching/preisstrategie-marktpositionierung",
    "Geschäftsmodell-Entwicklung": "/business-coaching/geschaeftsmodell-entwicklung",
    "Zielsetzung & Erfolgsmessung": "/business-coaching/zielsetzung-erfolgsmessung",
  };

  return (
    <section id="services" className="py-32 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-[0.03]"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold gradient-heading mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Meine Leistungen
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-accent to-purple-accent mx-auto rounded-full mb-8"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          ></motion.div>
          <motion.p 
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Umfassende Beratung und Begleitung für Ihren unternehmerischen Erfolg – 
            von der Strategie bis zur Umsetzung.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              <SpotlightCard className="h-full border-2 border-border/50 hover:border-accent/30 transition-all duration-300 shadow-[var(--shadow-soft)] hover:shadow-[0_12px_40px_-8px_hsla(190,64%,51%,0.25)] bg-gradient-card group overflow-hidden relative">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundImage: `linear-gradient(to right, hsl(var(--accent)), hsl(var(--purple-accent)))` }}></div>
                <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b opacity-100 transition-opacity duration-300" style={{ backgroundImage: `linear-gradient(to bottom, hsl(var(--accent)), hsl(var(--purple-accent)))` }}></div>
                
                <CardHeader className="pb-6">
                  <div className="flex items-start gap-4 mb-4">
                    <motion.div 
                      className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center shadow-[var(--shadow-soft)]`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <service.icon className="w-7 h-7 text-white" strokeWidth={2} />
                    </motion.div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl font-bold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">{service.title}</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-muted-foreground text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <motion.li 
                        key={featureIndex} 
                        className="flex items-start gap-2 text-sm text-muted-foreground"
                        initial={{ opacity: 0, x: -10 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.3, delay: 0.5 + index * 0.1 + featureIndex * 0.05 }}
                      >
                        <ArrowRight className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                        <span>
                          {businessFeaturePaths[feature] ? (
                            <a href={businessFeaturePaths[feature]} className="hover:text-foreground transition-[var(--transition-smooth)]">
                              {feature}
                            </a>
                          ) : feature === "INQA-Coaching – bis zu 80 % Förderung" ? (
                            <a href="/inqa-coaching" className="hover:text-foreground transition-[var(--transition-smooth)]">
                              {feature}
                            </a>
                          ) : feature === "AVGS (Aktivierungs- und Vermittlungsgutschein) – 100 % gefördert" ? (
                            <a href="/avgs-coaching" className="hover:text-foreground transition-[var(--transition-smooth)]">
                              {feature}
                            </a>
                          ) : (
                            feature
                          )}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full group/btn border-2 hover:border-accent" asChild>
                    <a href={service.link}>
                      Mehr erfahren
                      <motion.span
                        className="ml-2 inline-block"
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                      >
                        →
                      </motion.span>
                    </a>
                  </Button>
                </CardContent>
              </SpotlightCard>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-gradient-card rounded-2xl p-12 max-w-3xl mx-auto shadow-[var(--shadow-medium)] border-2 border-border/50 relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-accent to-purple-accent opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Individuelle Beratung gewünscht?
            </h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Lassen Sie uns in einem kostenlosen Erstgespräch herausfinden, 
              wie ich Sie optimal unterstützen kann.
            </p>
            <Button variant="cta" size="lg" className="px-10 py-6 text-lg" asChild>
              <a href="https://outlook.office365.com/book/Grndungsberatung@walbee.de/?ismsaljsauthenabled=true" target="_blank" rel="noopener noreferrer">
                <Calendar className="mr-2 w-5 h-5" />
                Zum Termin
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
